import {
  Image,
  Menu,
  MenuTrigger,
  MenuTriggerChildProps,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  EditRegular,
  MoreHorizontalRegular,
  StarFilled,
} from '@fluentui/react-icons';
import { SchoolLink as SchoolLinkTeamsServiceType } from '@mnspro-cloud/teams-services';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button/Button';
import { Text } from '../../Text/Text';
import { MaterialIcon } from '../../IconPicker/IconRegistry';
import { ThemeMode, useProviderTheme } from '../../hooks';
import { isUrl } from '../../Utils';
import './LinkTile.scss';
import { SchoolLink } from './SchoolLinks.interface';
import { getLinkTileBgColor } from './SchoolLinks.utils';

export interface LinkTileProps {
  link: SchoolLink | SchoolLinkTeamsServiceType;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  showFavoriteStar?: boolean;
  /**
   * Handler for opening the link. When omitted, falls back to
   * `window.open(url, '_blank')`. Teams consumers pass a handler based on
   * `app.openLink` so opening a link never tears the Teams tab.
   */
  onOpenLink?: (url: string) => void;
}

const openLinkFallback = (url: string) =>
  window.open(url, url.includes(window.location.hostname) ? '_self' : '_blank');

export const LinkTile: React.FC<LinkTileProps> = ({
  link,
  onDeleteClick,
  onEditClick,
  showFavoriteStar = true,
  onOpenLink,
}) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Only apply custom inline styles when a custom color is set
  const customColorStyle = useMemo(() => {
    if (theme.mode === ThemeMode.HighContrast) {
      return {
        backgroundColor: undefined,
        borderColor: undefined,
      };
    }

    if (link.image?.color) {
      return {
        backgroundColor: getLinkTileBgColor(link.image.color),
        borderColor: link.image.color,
      };
    }
    return;
  }, [link.image?.color]);

  const handleClick = useCallback(() => {
    if (isUrl(link.url)) {
      (onOpenLink ?? openLinkFallback)(link.url);
    }
  }, [link.url, onOpenLink]);

  return (
    <div
      aria-label={t('schoollinks:navigation_label', {
        defaultValue: 'Navigation zum externen Link: {{title}}',
        title: link.title,
      })}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={classNames('linkTile', {
        dark: theme.mode === ThemeMode.Dark,
      })}
      style={customColorStyle}
    >
      <div className="topBar flex justify-between items-center">
        {showFavoriteStar && link.isFavorite ? (
          <div className="flex">
            <StarFilled fontSize={24} className="favoriteStar" />
          </div>
        ) : (
          <div></div>
        )}

        {(onDeleteClick || onEditClick) && (
          <Menu open={menuOpen} onOpenChange={(_event, data) => setMenuOpen(data.open)}>
            <MenuTrigger disableButtonEnhancement>
              {(props: MenuTriggerChildProps<'button'>) => (
                <Button
                  {...props}
                  icon={<MoreHorizontalRegular />}
                  appearance="transparent"
                  aria-label={t('schoollinks:edit_trigger_aria_label', {
                    defaultValue: 'Öffnet ein Menü um den Schullink "{{title}}" zu bearbeiten',
                    title: link.title,
                  })}
                />
              )}
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {onEditClick && (
                  <MenuItem icon={<EditRegular />} onClick={onEditClick}>
                    {t('edit', { defaultValue: 'Bearbeiten' })}
                  </MenuItem>
                )}
                {onDeleteClick && (
                  <MenuItem icon={<DeleteRegular />} onClick={onDeleteClick}>
                    {t('delete', { defaultValue: 'Löschen' })}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>
      <div
        className="inner flex flex-col gap-2 items-center justify-center"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
          }
        }}
      >
        <div className="iconContainer flex flex-col items-center justify-center">
          {link.image?.imageUrl ? (
            <Image
              fit="contain"
              alt={t('schoollinks:alttext_label', {
                defaultValue: 'Icon der Webseite: {{title}}',
                title: link.title,
              })}
              src={link.image.imageUrl}
              className="linkImage"
            />
          ) : (
            <MaterialIcon
              name={link.image?.iconName ?? 'open_in_new'}
              className="linkIcon"
              style={link.image?.color ? { color: link.image.color } : undefined}
            />
          )}
          <Text className="text">{link.title}</Text>
        </div>
      </div>
    </div>
  );
};
